import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Flex, Text, Button, Badge, Spinner, Center, HStack, VStack, Select,
  IconButton, Tooltip, useToast, SimpleGrid, Menu, MenuButton, MenuList, MenuItem,
  Table, Thead, Tbody, Tr, Th, Td, Collapse,
} from "@chakra-ui/react";
import { Phone, MessageSquare, CheckCircle, Clock, RefreshCw, Plus, Contact, Download, ChevronDown, ClipboardCheck, Repeat, BookOpen } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { getCrmTarefasBoard, atualizarCrmTarefa, criarCrmContato, exportarCrmTarefas, getCrmTarefasMetricas } from "../../services/api";
import { useSoftphone } from "../../contexts/softphone";
import { formatPhone, paraDiscagem, soDigitos, formatDate, formatDateTime, prioridadeInfo, tarefaTipoInfo, pct } from "./crmUtils";
import RegistrarAtividadeModal from "./RegistrarAtividadeModal";
import NovaTarefaModal from "./NovaTarefaModal";
import DesfechoTarefaModal from "./DesfechoTarefaModal";
import ComoUsarTarefaModal from "./ComoUsarTarefaModal";

const ACCENT = "#F68633";

// As 4 colunas do Kanban (item 3 V1.5). 'adiada' (legado) chega agrupada em "pendente".
const COLUNAS = [
  { key: "pendente", label: "Pendentes", cor: "orange" },
  { key: "em_andamento", label: "Em andamento", cor: "blue" },
  { key: "feita", label: "Concluídas", cor: "green" },
  { key: "bloqueada", label: "Bloqueadas", cor: "red" },
];
const VAZIO = { pendente: [], em_andamento: [], feita: [], bloqueada: [] };

/**
 * Métricas de execução (item 5 da V1.7). É o detalhe por trás do card "Tarefas
 * concluídas" do painel Metas de hoje — não um segundo placar: aqui as taxas
 * mostram ONDE a tarefa parou (esforço, alcance ou eficácia).
 */
function MetricasTarefas({ dados }) {
  if (!dados?.geral?.total) return null;
  const g = dados.geral;
  const cartoes = [
    { label: "Taxa de tentativa", valor: g.taxa_tentativa, detalhe: `${g.tentou} de ${g.total} tocadas`, dica: "esforço do vendedor" },
    { label: "Taxa de resposta", valor: g.taxa_resposta, detalhe: `${g.respondido} de ${g.tentou} responderam`, dica: "eficácia de alcance" },
    { label: "Taxa de cumprimento", valor: g.taxa_cumprimento, detalhe: `${g.cumprido} de ${g.respondido} cumpriram`, dica: "eficácia da tarefa" },
    { label: "Aderência ao SLA", valor: g.aderencia_sla, detalhe: `${g.no_prazo} de ${g.total_auto} automáticas`, dica: "dentro do prazo" },
  ];

  return (
    <Box borderWidth="1px" borderColor="gray.100" borderRadius="xl" p={3} mb={4} bg="gray.50">
      <Text fontSize="xs" color="gray.500" mb={2}>
        Execução das tarefas nos últimos 30 dias (por tarefa criada no período).
      </Text>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={3}>
        {cartoes.map((c) => (
          <Box key={c.label} bg="white" borderWidth="1px" borderColor="gray.100" borderRadius="lg" p={3}>
            <Text fontSize="xs" color="gray.600" noOfLines={1}>{c.label}</Text>
            <Text fontSize="xl" fontWeight="bold" lineHeight={1.2}>{pct(c.valor)}</Text>
            <Text fontSize="11px" color="gray.400" noOfLines={1}>{c.detalhe}</Text>
            <Text fontSize="11px" color="gray.300" noOfLines={1}>{c.dica}</Text>
          </Box>
        ))}
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, xl: dados.por_vendedor?.length > 1 ? 2 : 1 }} spacing={3}>
        <TabelaTaxas
          titulo="Por tarefa"
          primeiraColuna="Tarefa"
          linhas={dados.por_tipo}
          chave={(l) => l.tipo}
          rotulo={(l) => `${l.codigo} ${l.nome}`}
        />
        {/* Por vendedor só aparece quando há mais de um em cena (admin sem filtro). */}
        {dados.por_vendedor?.length > 1 && (
          <TabelaTaxas
            titulo="Por vendedor"
            primeiraColuna="Vendedor"
            linhas={dados.por_vendedor}
            chave={(l) => l.vendedor_user_id ?? "sem"}
            rotulo={(l) => l.vendedor_nome}
          />
        )}
      </SimpleGrid>
    </Box>
  );
}

/** Tabela das 3 taxas + SLA, usada tanto por tarefa quanto por vendedor. */
function TabelaTaxas({ titulo, primeiraColuna, linhas, chave, rotulo }) {
  if (!linhas?.length) return null;
  return (
    <Box>
      <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>{titulo}</Text>
      <Box overflowX="auto">
        <Table size="sm" variant="simple" bg="white" borderRadius="lg">
          <Thead>
            <Tr>
              <Th fontSize="10px">{primeiraColuna}</Th>
              <Th fontSize="10px" isNumeric>Total</Th>
              <Th fontSize="10px" isNumeric>Tentativa</Th>
              <Th fontSize="10px" isNumeric>Resposta</Th>
              <Th fontSize="10px" isNumeric>Cumprimento</Th>
              <Th fontSize="10px" isNumeric>SLA</Th>
            </Tr>
          </Thead>
          <Tbody>
            {linhas.map((l) => (
              <Tr key={chave(l)}>
                <Td fontSize="xs">{rotulo(l)}</Td>
                <Td fontSize="xs" isNumeric>{l.total}</Td>
                <Td fontSize="xs" isNumeric>{pct(l.taxa_tentativa)}</Td>
                <Td fontSize="xs" isNumeric>{pct(l.taxa_resposta)}</Td>
                <Td fontSize="xs" isNumeric>{pct(l.taxa_cumprimento)}</Td>
                <Td fontSize="xs" isNumeric>{pct(l.aderencia_sla)}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
}

export default function TarefasTab({ onAbrirChat, onAbrirFicha, refreshKey }) {
  const [cols, setCols] = useState(VAZIO);
  const [vendedores, setVendedores] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exportando, setExportando] = useState(false);
  const [abrindo, setAbrindo] = useState(null);
  const [ativTarefa, setAtivTarefa] = useState(null);
  const [desfechoTarefa, setDesfechoTarefa] = useState(null);
  const [comoUsarTarefa, setComoUsarTarefa] = useState(null);
  const [novaOpen, setNovaOpen] = useState(false);
  const [metricas, setMetricas] = useState(null);
  const [verMetricas, setVerMetricas] = useState(false);
  const [catalogo, setCatalogo] = useState({});
  // Filtro padrão: as tarefas do próprio usuário logado (admin pode ver de todos).
  const [vendedor, setVendedor] = useState(
    () => String(JSON.parse(window.localStorage.getItem("user") || "{}")?.id || "")
  );
  const sp = useSoftphone();
  const toast = useToast();

  const carregar = useCallback(async () => {
    setLoading(true);
    const [r, m] = await Promise.all([
      getCrmTarefasBoard({ vendedor_id: vendedor || undefined }),
      getCrmTarefasMetricas({ vendedor_id: vendedor || undefined }),
    ]);
    setCols({ ...VAZIO, ...(r?.data || {}) });
    setVendedores(r?.vendedores || []);
    setIsAdmin(!!r?.is_admin);
    setCatalogo(r?.catalogo || {});
    setMetricas(m || null);
    setLoading(false);
  }, [vendedor]);

  useEffect(() => { carregar(); }, [carregar, refreshKey]);

  // Exporta as tarefas do vendedor filtrado, com os dados do cliente da ficha.
  const exportar = async (formato) => {
    setExportando(true);
    const blob = await exportarCrmTarefas({ vendedor_id: vendedor || undefined, formato });
    setExportando(false);

    if (!blob) { toast({ status: "error", title: "Erro ao exportar as tarefas" }); return; }

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `crm-tarefas-${new Date().toISOString().slice(0, 10)}.${formato === "csv" ? "csv" : "xlsx"}`;
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Mover entre colunas = trocar o status (não há ordenação persistida dentro da coluna).
  const onDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination || source.droppableId === destination.droppableId) return;
    const fromKey = source.droppableId;
    const toKey = destination.droppableId;
    const fromList = Array.from(cols[fromKey] || []);
    const [moved] = fromList.splice(source.index, 1);
    if (!moved) return;
    const toList = Array.from(cols[toKey] || []);
    toList.splice(destination.index, 0, { ...moved, status: toKey });
    setCols({ ...cols, [fromKey]: fromList, [toKey]: toList });

    const r = await atualizarCrmTarefa(moved.id, { status: toKey });
    if (!r?.data) { toast({ status: "error", title: "Não foi possível mover a tarefa" }); carregar(); }
  };

  const ligar = (t) => {
    if (soDigitos(t.telefone).length < 10) { toast({ status: "warning", title: "Sem telefone válido" }); return; }
    if (!sp.registered) { toast({ status: "warning", title: "Ramal desconectado", description: "Conecte o softphone no Discador." }); return; }
    sp.dial(paraDiscagem(t.telefone));
    toast({ status: "info", title: "Discando...", description: formatPhone(t.telefone) });
  };

  const conversar = async (t) => {
    if (soDigitos(t.telefone).length < 10) { toast({ status: "warning", title: "Sem telefone válido" }); return; }
    setAbrindo(t.id);
    const r = await criarCrmContato({ telefone: t.telefone, nome: t.cliente_nome, origem: "tarefa" });
    setAbrindo(null);
    if (r?.data?.id) onAbrirChat(r.data.id);
    else toast({ status: "error", title: "Erro", description: r?.error });
  };

  // Vencida: o SLA é contado em horas, então prazo_em manda quando existe.
  const venceu = (t) => {
    if (t.status === "feita") return false;
    if (t.prazo_em) return new Date(t.prazo_em) < new Date();
    return !!t.prazo && new Date(t.prazo) < new Date(new Date().toDateString());
  };
  const total = COLUNAS.reduce((acc, c) => acc + (cols[c.key]?.length || 0), 0);

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4} gap={2} flexWrap="wrap">
        <Text fontSize="sm" color="gray.500">
          As tarefas do fluxo chegam sozinhas, de hora em hora. Arraste entre as colunas para mudar o andamento.
        </Text>
        <HStack flexWrap="wrap">
          {isAdmin && (
            <Select size="sm" maxW="190px" bg="white" placeholder="Todos vendedores" value={vendedor} onChange={(e) => setVendedor(e.target.value)}>
              {vendedores.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </Select>
          )}
          <Menu>
            <MenuButton
              as={Button} size="sm" variant="outline" colorScheme="green"
              leftIcon={<Download size={15} />} rightIcon={<ChevronDown size={14} />}
              isLoading={exportando} loadingText="Gerando" isDisabled={total === 0}
            >
              Exportar
            </MenuButton>
            <MenuList minW="180px">
              <MenuItem fontSize="sm" onClick={() => exportar("xlsx")}>Excel (.xlsx)</MenuItem>
              <MenuItem fontSize="sm" onClick={() => exportar("csv")}>CSV (.csv)</MenuItem>
            </MenuList>
          </Menu>
          <Button size="sm" variant="ghost" leftIcon={<RefreshCw size={15} />} onClick={carregar}>Atualizar</Button>
          <Button size="sm" variant="ghost" onClick={() => setVerMetricas((v) => !v)}>
            {verMetricas ? "Ocultar métricas" : "Métricas"}
          </Button>
          <Button size="sm" colorScheme="orange" leftIcon={<Plus size={15} />} onClick={() => setNovaOpen(true)}>Nova tarefa</Button>
        </HStack>
      </Flex>

      <Collapse in={verMetricas} animateOpacity>
        <MetricasTarefas dados={metricas} />
      </Collapse>

      {loading ? (
        <Center py={16}><Spinner color={ACCENT} /></Center>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={3} alignItems="start">
            {COLUNAS.map((col) => {
              const lista = cols[col.key] || [];
              return (
                <Droppable droppableId={col.key} key={col.key}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      bg={snapshot.isDraggingOver ? "orange.50" : "gray.50"}
                      borderWidth="1px"
                      borderColor={snapshot.isDraggingOver ? "orange.200" : "gray.100"}
                      borderRadius="xl"
                      p={2}
                      minH="120px"
                      transition="background 0.12s"
                    >
                      <HStack mb={2} px={1} justify="space-between">
                        <HStack spacing={2}>
                          <Box w="8px" h="8px" borderRadius="full" bg={`${col.cor}.400`} />
                          <Text fontWeight="bold" fontSize="sm">{col.label}</Text>
                        </HStack>
                        <Badge colorScheme={col.cor} borderRadius="full">{lista.length}</Badge>
                      </HStack>

                      <VStack align="stretch" spacing={2}>
                        {lista.map((t, idx) => (
                          <Draggable draggableId={String(t.id)} index={idx} key={t.id}>
                            {(prov, snap) => (
                              <Box
                                ref={prov.innerRef}
                                {...prov.draggableProps}
                                {...prov.dragHandleProps}
                                bg="white"
                                borderWidth="1px"
                                borderColor={venceu(t) ? "red.200" : "gray.100"}
                                borderRadius="lg"
                                p={3}
                                boxShadow={snap.isDragging ? "lg" : "xs"}
                                _hover={{ boxShadow: "sm" }}
                              >
                                <HStack spacing={2} mb={1} flexWrap="wrap">
                                  <Text fontSize="sm" fontWeight="semibold" noOfLines={1} flex="1" minW={0}>{t.cliente_nome || "Cliente"}</Text>
                                  {tarefaTipoInfo(t.tipo) && (
                                    <Tooltip label={`${catalogo[t.tipo]?.nome || tarefaTipoInfo(t.tipo).curto} — clique para ver como fazer`}>
                                      <Badge
                                        variant="subtle" colorScheme="purple" cursor="pointer"
                                        _hover={{ bg: "purple.100" }}
                                        onClick={() => setComoUsarTarefa(t)}
                                      >
                                        {tarefaTipoInfo(t.tipo).codigo}
                                      </Badge>
                                    </Tooltip>
                                  )}
                                  {t.origem === "regra" && <Badge variant="outline" colorScheme="orange">auto</Badge>}
                                  {Number(t.prioridade) > 0 && (
                                    <Badge colorScheme={prioridadeInfo(t.prioridade).cor} variant="subtle">
                                      {prioridadeInfo(t.prioridade).label.toLowerCase()}
                                    </Badge>
                                  )}
                                </HStack>
                                <Text fontSize="sm" color="gray.700" noOfLines={3}>{t.motivo}</Text>
                                <HStack fontSize="xs" color={venceu(t) ? "red.500" : "gray.400"} mt={1} spacing={3} flexWrap="wrap">
                                  {(t.prazo_em || t.prazo) && (
                                    <HStack spacing={1}>
                                      <Clock size={12} />
                                      <Text>{t.prazo_em ? formatDateTime(t.prazo_em) : formatDate(t.prazo)}</Text>
                                    </HStack>
                                  )}
                                  <Text>{formatPhone(t.telefone) || "sem telefone"}</Text>
                                </HStack>
                                {Number(t.tentativas) > 0 && (
                                  <HStack fontSize="11px" color="orange.500" mt={1} spacing={1}>
                                    <Repeat size={11} />
                                    <Text>
                                      {t.tentativas}ª tentativa sem resposta
                                      {t.proxima_tentativa_em ? ` · retomar em ${formatDate(t.proxima_tentativa_em)}` : ""}
                                    </Text>
                                  </HStack>
                                )}
                                {t.criado_por_nome && t.criado_por_user_id !== t.vendedor_user_id && (
                                  <Text fontSize="11px" color="gray.400" mt={1}>criada por {t.criado_por_nome}</Text>
                                )}
                                <HStack spacing={1} mt={2} justify="end">
                                  {catalogo[t.tipo] && (
                                    <Tooltip label="Como fazer esta tarefa (canal, abordagem e cuidados)">
                                      <IconButton aria-label="Como fazer esta tarefa" size="xs" variant="ghost" colorScheme="purple" icon={<BookOpen size={15} />} onClick={() => setComoUsarTarefa(t)} />
                                    </Tooltip>
                                  )}
                                  <Tooltip label="Abrir ficha"><IconButton aria-label="Abrir ficha" size="xs" variant="ghost" icon={<Contact size={15} />} onClick={() => onAbrirFicha(t.cliente_id)} /></Tooltip>
                                  <Tooltip label="Ligar"><IconButton aria-label="Ligar" size="xs" variant="ghost" colorScheme="green" icon={<Phone size={15} />} onClick={() => ligar(t)} /></Tooltip>
                                  <Tooltip label="Conversar"><IconButton aria-label="Conversar" size="xs" variant="ghost" colorScheme="orange" icon={<MessageSquare size={15} />} isLoading={abrindo === t.id} onClick={() => conversar(t)} /></Tooltip>
                                  {t.status !== "feita" && (
                                    <>
                                      <Tooltip label="Registrar desfecho (esforço, contato e objetivo)">
                                        <IconButton aria-label="Registrar desfecho" size="xs" variant="ghost" colorScheme="purple" icon={<ClipboardCheck size={15} />} onClick={() => setDesfechoTarefa(t)} />
                                      </Tooltip>
                                      <Tooltip label="Registrar atividade"><IconButton aria-label="Registrar atividade" size="xs" variant="ghost" colorScheme="green" icon={<CheckCircle size={15} />} onClick={() => setAtivTarefa(t)} /></Tooltip>
                                    </>
                                  )}
                                </HStack>
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {lista.length === 0 && <Text fontSize="xs" color="gray.300" textAlign="center" py={4}>—</Text>}
                      </VStack>
                    </Box>
                  )}
                </Droppable>
              );
            })}
          </SimpleGrid>
        </DragDropContext>
      )}

      {!loading && total === 0 && (
        <Center py={8} flexDirection="column" color="gray.400">
          <Text fontSize="sm">Nenhuma tarefa por aqui.</Text>
          <Text fontSize="xs">As automáticas chegam sozinhas quando o gatilho bate; use "Nova tarefa" para criar uma agora.</Text>
        </Center>
      )}

      <RegistrarAtividadeModal
        isOpen={!!ativTarefa}
        onClose={() => setAtivTarefa(null)}
        clienteId={ativTarefa?.cliente_id}
        clienteNome={ativTarefa?.cliente_nome}
        tarefa={ativTarefa}
        onSaved={carregar}
      />
      <DesfechoTarefaModal
        isOpen={!!desfechoTarefa}
        onClose={() => setDesfechoTarefa(null)}
        tarefa={desfechoTarefa}
        info={catalogo[desfechoTarefa?.tipo]}
        onSaved={carregar}
      />
      <ComoUsarTarefaModal
        isOpen={!!comoUsarTarefa}
        onClose={() => setComoUsarTarefa(null)}
        tarefa={comoUsarTarefa}
        info={catalogo[comoUsarTarefa?.tipo]}
        onRegistrarDesfecho={
          comoUsarTarefa && comoUsarTarefa.status !== "feita"
            ? () => { setDesfechoTarefa(comoUsarTarefa); setComoUsarTarefa(null); }
            : undefined
        }
      />
      <NovaTarefaModal
        isOpen={novaOpen}
        onClose={() => setNovaOpen(false)}
        onCreated={carregar}
        vendedores={vendedores}
      />
    </Box>
  );
}
